import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { 
  ShoppingCartIcon, 
  CheckCircleIcon, 
  ClockIcon,
  ExclamationCircleIcon,
  CurrencyDollarIcon,
  TagIcon,
  TrashIcon,
  DocumentDuplicateIcon,
  MagnifyingGlassIcon,
  CreditCardIcon
} from '@heroicons/react/24/outline';
import api from '../services/api';
import toast from 'react-hot-toast';

function CookieList() {
  const [filters, setFilters] = useState({
    status: '',
    service: '',
    search: ''
  });
  const [selectedCookie, setSelectedCookie] = useState(null);
  const [showCardSelector, setShowCardSelector] = useState(null);
  const [selectedCardId, setSelectedCardId] = useState(null);
  const queryClient = useQueryClient();

  // Obtener cookies
  const { data: cookiesData, isLoading } = useQuery(
    ['cookies', filters],
    async () => {
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.service) params.append('service', filters.service);
      if (filters.search) params.append('search', filters.search);
      
      const response = await api.get(`/cookies?${params}`);
      return response.data;
    }
  );

  // Obtener tarjetas
  const { data: cardsData } = useQuery('cards', async () => {
    const response = await api.get('/cards');
    return response.data;
  });

  const deleteMutation = useMutation(
    (id) => api.delete(`/cookies/${id}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('cookies');
        queryClient.invalidateQueries('stats');
        toast.success('🗑️ Cookie eliminada');
      },
      onError: (error) => {
        toast.error(error.response?.data?.error || 'Error al eliminar');
      }
    }
  );

  // 🔧 MUTACIÓN DE PAGO CORREGIDA
  const payMutation = useMutation(
    ({ cookieId, cardId }) => {
      console.log('📤 Enviando pago con cardId:', cardId);
      return api.post(`/cookies/${cookieId}/pay`, { cardId });
    },
    {
      onSuccess: (data) => {
        queryClient.invalidateQueries('cookies');
        queryClient.invalidateQueries('stats');
        
        const payment = data.data.payment;
        toast.success(
          <div>
            <p className="font-bold">✅ Pago exitoso!</p>
            <p className="text-sm">💰 Monto: ${payment.amount}</p>
            <p className="text-sm">💳 Tarjeta: •••• {payment.cardLastFour}</p>
            <p className="text-sm">📊 Usos restantes: {payment.remainingUses}</p>
          </div>,
          { duration: 5000 }
        );
        setShowCardSelector(null);
        setSelectedCardId(null);
      },
      onError: (error) => {
        console.error('❌ Error en pago:', error.response?.data);
        toast.error(error.response?.data?.error || '❌ Error al procesar pago');
      }
    }
  );

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('📋 Cookie copiada al portapapeles');
  };

  const handlePayClick = (cookieId) => {
    const cards = cardsData?.cards || [];
    if (cards.length === 0) {
      toast.error('No tienes tarjetas guardadas. Ve a "Tarjetas" y agrega una.');
      return;
    }
    setShowCardSelector(cookieId);
    setSelectedCardId(null); // Resetear selección
  };

  const confirmPayment = (cookieId) => {
    console.log('🔍 Confirmando pago - selectedCardId:', selectedCardId);
    if (!selectedCardId) {
      toast.error('Selecciona una tarjeta');
      return;
    }
    payMutation.mutate({ cookieId, cardId: selectedCardId });
  };

  const getStatusIcon = (status) => {
    switch(status) {
      case 'activa':
        return <ClockIcon className="h-5 w-5 text-green-500" />;
      case 'usada':
        return <CheckCircleIcon className="h-5 w-5 text-gray-400" />;
      case 'expirada':
        return <ExclamationCircleIcon className="h-5 w-5 text-red-500" />;
      default:
        return <ShoppingCartIcon className="h-5 w-5 text-gray-400" />;
    }
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cyan-500"></div>
      </div>
    );
  }

  const cookies = cookiesData?.cookies || [];
  const cards = cardsData?.cards || [];

  return (
    <div style={{ maxWidth: '80rem', margin: '0 auto', padding: '1rem', paddingBottom: '6rem' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2 style={{ fontSize: '1.5rem', fontWeight: 'bold', color: '#ffffff' }}>Mis Cookies de Pago</h2>
        <div style={{ background: 'rgba(0,255,255,0.1)', padding: '0.25rem 0.75rem', borderRadius: '1rem', color: '#00ffff' }}>
          Total: {cookies.length} cookies
        </div>
      </div>

      {/* Filtros */}
      <div style={{ background: '#111111', borderRadius: '1rem', padding: '1rem', marginBottom: '1.5rem', border: '1px solid #222222' }}>
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
          <div style={{ flex: 1, minWidth: '200px' }}>
            <div style={{ position: 'relative' }}>
              <MagnifyingGlassIcon style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)', width: '1.25rem', height: '1.25rem', color: '#9ca3af' }} />
              <input
                type="text"
                placeholder="Buscar cookies..."
                value={filters.search}
                onChange={(e) => setFilters({...filters, search: e.target.value})}
                style={{ width: '100%', padding: '0.75rem 0.75rem 0.75rem 2.5rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.75rem', color: '#ffffff' }}
              />
            </div>
          </div>
          
          <select
            value={filters.status}
            onChange={(e) => setFilters({...filters, status: e.target.value})}
            style={{ padding: '0.75rem 1rem', background: '#1a1a1a', border: '1px solid #333333', borderRadius: '0.75rem', color: '#ffffff' }}
          >
            <option value="">Todos los estados</option>
            <option value="activa">Activas</option>
            <option value="usada">Usadas</option>
            <option value="expirada">Expiradas</option>
          </select>

          {(filters.status || filters.service || filters.search) && (
            <button
              onClick={() => setFilters({status: '', service: '', search: ''})}
              style={{ padding: '0.75rem 1rem', background: 'none', border: '1px solid #ff0033', borderRadius: '0.75rem', color: '#ff0033', cursor: 'pointer' }}
            >
              Limpiar filtros
            </button>
          )}
        </div>
      </div>

      {/* Lista de cookies */}
      <div style={{ background: '#111111', borderRadius: '1rem', border: '1px solid #222222', overflow: 'hidden' }}>
        {cookies.length > 0 ? (
          <ul style={{ listStyle: 'none' }}>
            {cookies.map((cookie) => (
              <li 
                key={cookie.id} 
                style={{ padding: '1rem', borderBottom: '1px solid #222222', cursor: 'pointer', transition: 'background 0.2s' }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#1a1a1a'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                onClick={() => setSelectedCookie(selectedCookie === cookie.id ? null : cookie.id)}
              >
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                  <div style={{ marginTop: '0.25rem' }}>
                    {getStatusIcon(cookie.status)}
                  </div>
                  
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem' }}>
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
                          <span style={{ fontSize: '1.25rem', fontWeight: 'bold', color: '#00ffff' }}>
                            ${cookie.amount}
                          </span>
                          <span style={{ background: cookie.service === 'amazon' ? '#ff990020' : '#333333', padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.75rem', color: cookie.service === 'amazon' ? '#ff9900' : '#9ca3af' }}>
                            {cookie.service}
                          </span>
                          <span style={{ padding: '0.25rem 0.75rem', borderRadius: '1rem', fontSize: '0.75rem', background: cookie.status === 'activa' ? '#ccff0020' : '#333333', color: cookie.status === 'activa' ? '#ccff00' : '#9ca3af' }}>
                            {cookie.status}
                          </span>
                        </div>
                        
                        {cookie.notes && (
                          <p style={{ fontSize: '0.875rem', color: '#9ca3af', marginTop: '0.25rem' }}>
                            📝 {cookie.notes}
                          </p>
                        )}
                      </div>
                      
                      <div style={{ fontSize: '0.75rem', color: '#9ca3af' }}>
                        🕐 {format(new Date(cookie.createdAt), "dd/MM/yyyy HH:mm", { locale: es })}
                      </div>
                    </div>

                    {/* Selector de tarjetas */}
                    {showCardSelector === cookie.id && (
                      <div style={{ marginTop: '1rem', padding: '1rem', background: '#1a1a1a', borderRadius: '0.75rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '1rem' }}>
                          <CreditCardIcon style={{ width: '1.25rem', height: '1.25rem', color: '#00ffff' }} />
                          <span style={{ color: '#ffffff', fontWeight: 'bold' }}>Selecciona una tarjeta</span>
                        </div>
                        
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '1rem' }}>
                          {cards.map(card => (
                            <label
                              key={card.id}
                              style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '1rem',
                                padding: '0.75rem',
                                background: selectedCardId === card.id ? '#00ffff20' : '#0a0a0a',
                                border: selectedCardId === card.id ? '1px solid #00ffff' : '1px solid #333333',
                                borderRadius: '0.5rem',
                                cursor: 'pointer'
                              }}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="radio"
                                name={`card-${cookie.id}`}
                                value={card.id}
                                checked={selectedCardId === card.id}
                                onChange={() => {
                                  console.log('✅ Tarjeta seleccionada:', card.id);
                                  setSelectedCardId(card.id);
                                }}
                                style={{ accentColor: '#00ffff' }}
                              />
                              <div>
                                <div style={{ fontWeight: 'bold', color: '#ffffff' }}>{card.brand.toUpperCase()} •••• {card.lastFour}</div>
                                <div style={{ fontSize: '0.7rem', color: '#9ca3af' }}>Expira: {card.expiryMonth}/{card.expiryYear}</div>
                              </div>
                            </label>
                          ))}
                        </div>
                        
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              setShowCardSelector(null);
                              setSelectedCardId(null);
                            }}
                            style={{ flex: 1, padding: '0.5rem', background: '#333333', border: 'none', borderRadius: '0.5rem', color: '#ffffff', cursor: 'pointer' }}
                          >
                            Cancelar
                          </button>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              console.log('🔘 Confirmar pago - selectedCardId:', selectedCardId);
                              confirmPayment(cookie.id);
                            }}
                            disabled={!selectedCardId || payMutation.isLoading}
                            style={{
                              flex: 1,
                              padding: '0.5rem',
                              background: 'linear-gradient(135deg, #00ffff, #0066ff)',
                              border: 'none',
                              borderRadius: '0.5rem',
                              color: '#000000',
                              fontWeight: 'bold',
                              cursor: (!selectedCardId || payMutation.isLoading) ? 'not-allowed' : 'pointer',
                              opacity: (!selectedCardId || payMutation.isLoading) ? 0.5 : 1
                            }}
                          >
                            {payMutation.isLoading ? 'Procesando...' : 'Confirmar Pago'}
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Acciones */}
                    {showCardSelector !== cookie.id && (
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
                        {cookie.status === 'activa' && (
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePayClick(cookie.id);
                            }}
                            style={{
                              padding: '0.5rem 1rem',
                              background: 'linear-gradient(135deg, #00ffff, #0066ff)',
                              border: 'none',
                              borderRadius: '0.5rem',
                              color: '#000000',
                              fontWeight: 'bold',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '0.5rem'
                            }}
                          >
                            <CurrencyDollarIcon style={{ width: '1rem', height: '1rem' }} />
                            Pagar
                          </button>
                        )}
                        
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            if (window.confirm('¿Eliminar esta cookie?')) {
                              deleteMutation.mutate(cookie.id);
                            }
                          }}
                          style={{
                            padding: '0.5rem 1rem',
                            background: '#1a1a1a',
                            border: '1px solid #ff0033',
                            borderRadius: '0.5rem',
                            color: '#ff0033',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}
                        >
                          <TrashIcon style={{ width: '1rem', height: '1rem' }} />
                          Eliminar
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <div style={{ textAlign: 'center', padding: '3rem 1.5rem', color: '#9ca3af' }}>
            <ShoppingCartIcon style={{ width: '4rem', height: '4rem', margin: '0 auto 1rem', opacity: 0.5 }} />
            <p>No hay cookies guardadas</p>
            <button
              onClick={() => window.location.href = '/add-cookie'}
              style={{ marginTop: '1rem', padding: '0.75rem 1.5rem', background: 'linear-gradient(135deg, #00ffff, #0066ff)', border: 'none', borderRadius: '2rem', color: '#000000', fontWeight: 'bold', cursor: 'pointer' }}
            >
              Agregar primera cookie
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

export default CookieList;